import { Button } from '@forwardflow/ui'
import { PreviousBillings } from './PreviousBillings'
import { NextBillingCycle } from './NextBillingCycle'
import { SavedCards } from './SavedCards'
import { useGetUserSubscription } from '@forwardflow/hooks/customHooks'

import { PauseSubscriptionModal } from '@forwardflow/modals/PauseSubscriptionModal'
import { usePauseSubscriptionModalStore } from '@forwardflow/store/modal/pauseSubscriptionModal'
import { useEffect, useState } from 'react'
import { UpdatePaymentModal } from '@forwardflow/modals/UpdatePaymentModal'
import { Skeleton } from 'antd'
import SubscriptionService from '@forwardflow/services/SubscriptionService'
import { useAppStore } from '@forwardflow/store'
import { CreditsUsed } from '@forwardflow/modules/settings/plans-and-billing'

const BillingAndSubscription = () => {
   const { data, isLoading, mutate } = useGetUserSubscription()
   const { openPauseSubscriptionModal } = usePauseSubscriptionModalStore()
   const { currentOrganization } = useAppStore()
   const [seats, setSeats] = useState(1)
   const [originalSeats, setOriginalSeats] = useState(1)
   const [isUpdatingSeats, setIsUpdatingSeats] = useState(false)

   const [subscriptionStatus, setSubscriptionStatus] = useState(
      data?.organizationSubscription?.status
   )

   useEffect(() => {
      setSubscriptionStatus(data?.organizationSubscription?.status)
   }, [data?.organizationSubscription?.status])

   // Initialize seats from subscription data
   useEffect(() => {
      if (data?.organizationSubscription) {
         const currentSeats = data.organizationSubscription.seatCounts || 1
         setSeats(currentSeats)
         setOriginalSeats(currentSeats)
      }
   }, [data?.organizationSubscription])

   const toggleSubscriptionStatus = async () => {
      openPauseSubscriptionModal()
   }

   const handleIncrementSeat = () => {
      const maxSeats =
         data?.organizationSubscription?.pricingTier?.maxSeats || 100
      if (seats < maxSeats) {
         setSeats((prev) => prev + 1)
      }
   }

   const handleDecrementSeat = () => {
      const minSeats =
         data?.organizationSubscription?.pricingTier?.minSeats || 1
      if (seats > minSeats) {
         setSeats((prev) => prev - 1)
      }
   }

   const handleSaveSeats = async () => {
      if (!currentOrganization || isUpdatingSeats || seats === originalSeats)
         return

      try {
         setIsUpdatingSeats(true)
         const seatDifference = seats - originalSeats

         if (seatDifference > 0) {
            // Add seats
            await SubscriptionService.addSeats(
               currentOrganization,
               seatDifference
            )
         } else {
            // Remove seats
            await SubscriptionService.removeSeats(
               currentOrganization,
               Math.abs(seatDifference)
            )
         }

         setOriginalSeats(seats)
         // Refresh subscription data
         mutate()
      } catch (error) {
         // Handle error silently or show notification
         // Revert to original seats on error
         setSeats(originalSeats)
      } finally {
         setIsUpdatingSeats(false)
      }
   }

   const handleCancelSeats = () => {
      setSeats(originalSeats)
   }

   const hasSeatsChanged = seats !== originalSeats

   // Calculate estimated cost
   const pricePerSeat =
      data?.organizationSubscription?.pricingTier?.monthlyPrice || 0
   const currentTotalCost = data?.nextBillingCycle?.totalPrice || 0
   const estimatedCost = seats * pricePerSeat
   const costDifference = estimatedCost - currentTotalCost

   return (
      <div className="flex flex-col flex-1 px-10 text-white gap-8">
         <div className="flex items-center justify-between py-6 pl-10 pr-4">
            <span className="mr-4 font-medium">Billing and Subscription</span>
            <Button
               onClick={toggleSubscriptionStatus}
               className={`!h-10 !rounded px-7 !mt-0 max-w-60 ${
                  subscriptionStatus === 'active'
                     ? '!bg-danger/20 hover:!bg-danger/50 !border-danger'
                     : '!bg-primary/10'
               }`}
            >
               {subscriptionStatus === 'active' ? 'Pause' : 'Resume'}
               &nbsp; Subscription
            </Button>
         </div>
         {isLoading ? (
            <div className="flex flex-col gap-4">
               <Skeleton active />
            </div>
         ) : (
            <div>
               {isLoading ? (
                  <div className="flex flex-col gap-4">
                     <Skeleton active />
                  </div>
               ) : (
                  <div className="bg-[#474747]/10  flex items-center border border-white/10 rounded-xl my-7 py-3 px-6 justify-between">
                     <p>
                        Credit balance:
                        <span className="text-[20px] font-medium">
                           {' $'}
                           {data?.creditInfo?.remainingCredits}
                        </span>
                     </p>
                     {/* <button
               onClick={openPurchaseCreditModal}
               className="px-5 py-2 transition-all border rounded bg-white/10 hover:bg-white/20 border-primary/10"
            >
               Purchase credits
            </button> */}
                  </div>
               )}
               <div className="bg-[#474747]/10 rounded-xl">
                  <div className="flex items-center justify-between py-6 px-9 text-[20px] font-medium bg-[#474747]/10">
                     <span>Subscription</span>
                     <span className="text-primary">
                        ${' '}
                        {
                           data?.organizationSubscription?.pricingTier
                              ?.monthlyPrice
                        }{' '}
                        / user / month
                     </span>
                  </div>

                  <p className="border-b border-b-white/10 mx-9"></p>
                  <div className="flex items-center justify-between py-6 font-medium px-9">
                     <span>Number of Users</span>
                     <div className="flex items-center gap-3">
                        <button
                           onClick={handleDecrementSeat}
                           disabled={
                              seats <=
                                 (data?.organizationSubscription?.pricingTier
                                    ?.minSeats || 1) || isUpdatingSeats
                           }
                           className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                           aria-label="Remove seat"
                        >
                           <span className="text-white text-lg">-</span>
                        </button>
                        <span className="text-primary text-[20px] min-w-[40px] text-center">
                           {seats}
                        </span>
                        <button
                           onClick={handleIncrementSeat}
                           disabled={
                              seats >=
                                 (data?.organizationSubscription?.pricingTier
                                    ?.maxSeats || 100) || isUpdatingSeats
                           }
                           className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                           aria-label="Add seat"
                        >
                           <span className="text-white text-lg">+</span>
                        </button>
                     </div>
                  </div>
                  {hasSeatsChanged && (
                     <div className="px-9 pb-4">
                        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                           <div className="flex items-center justify-between mb-2">
                              <span className="text-white/70 text-sm">
                                 Current cost ({originalSeats} user
                                 {originalSeats !== 1 ? 's' : ''})
                              </span>
                              <span className="text-white text-sm">
                                 ${currentTotalCost.toFixed(2)}/month
                              </span>
                           </div>
                           <div className="flex items-center justify-between mb-2">
                              <span className="text-white/70 text-sm">
                                 New cost ({seats} user{seats !== 1 ? 's' : ''})
                              </span>
                              <span className="text-white text-sm">
                                 ${estimatedCost.toFixed(2)}/month
                              </span>
                           </div>
                           <div className="border-t border-white/10 my-2"></div>
                           <div className="flex items-center justify-between">
                              <span className="text-white font-medium">
                                 {costDifference > 0 ? 'Additional' : 'Savings'}
                              </span>
                              <span
                                 className={`font-semibold ${
                                    costDifference > 0
                                       ? 'text-orange-400'
                                       : 'text-green-400'
                                 }`}
                              >
                                 {costDifference > 0 ? '+' : ''}$
                                 {Math.abs(costDifference).toFixed(2)}/month
                              </span>
                           </div>
                        </div>
                     </div>
                  )}
                  {hasSeatsChanged && (
                     <div>
                        <div className="flex items-center justify-end gap-3 px-9 pb-6">
                           <button
                              onClick={handleCancelSeats}
                              disabled={isUpdatingSeats}
                              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white text-sm font-medium"
                           >
                              Cancel
                           </button>
                           <button
                              onClick={handleSaveSeats}
                              disabled={isUpdatingSeats}
                              className="px-4 py-2 rounded-lg bg-primary hover:bg-primary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white text-sm font-medium"
                           >
                              {isUpdatingSeats ? 'Saving...' : 'Save Changes'}
                           </button>
                        </div>
                        <p className="mx-9 mb-2 text-sm text-white/70">
                           We use prorated billing to ensure you are only
                           charged for the exact portion of the service you use,
                           rather than the full billing cycle amount
                        </p>
                     </div>
                  )}
                  <p className="border-b border-b-white/10 mx-9"></p>
                  <div className="flex items-center justify-between py-6 font-medium px-9">
                     <span>Phone numbers ({data?.phoneNumbers?.count})</span>
                     <span className="text-primary text-[20px]">
                        $ {data?.phoneNumbers?.price} /{' '}
                        {data?.phoneNumbers?.period}
                     </span>
                  </div>
               </div>
            </div>
         )}
         {/* section for Credits used */}
         <CreditsUsed />
         {/* end of credits used section  */}
         <div className="flex gap-5 mt-10">
            <SavedCards />
            <NextBillingCycle />
         </div>
         <PreviousBillings />
         <PauseSubscriptionModal />
         <UpdatePaymentModal />
      </div>
   )
}

export default BillingAndSubscription
